import { BudgetResource, AppropriatedResource, OrcamentoData } from "@/services/orcamento";

/* ─── Tipos compartilhados ─────────────────────────────────────────────────── */

export interface CCStat {
  /** Nome da unidade construtiva (etapa Sienge) */
  ccName:    string;
  orcado:    number;
  realizado: number;
  saldo:     number;
  pct:       number;
}

export interface InsumoStat {
  resourceId:  string;
  description: string;
  /** Unidade construtiva vinculada ao insumo no orçamento */
  ccName:      string;
  /** ID Sienge da unidade construtiva (cruzamento com apropriações do pedido) */
  buildingUnitId?: string;
  /** Unidade de medida (ex.: m, kg) — orçamento; reforçada pela apropriação se vazia */
  unit:        string;
  /** Quantidade orçada (soma das linhas de orçamento no insumo × UC) */
  qtdOrcada:   number;
  /** Quantidade realizada/apropriada (soma no insumo × UC) */
  qtdRealizada: number;
  /** Quantidade orçada − realizada */
  saldoQtd:    number;
  /** % realizado em quantidade (0 se orçado em qtd. = 0) */
  pctQtd:      number;
  orcado:      number;
  realizado:   number;
  saldo:       number;
  pct:         number;
  /** URL da imagem do insumo (quando a API enviar) */
  imageUrl?:   string;
}

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

export const UC_SEM_LABEL = "Sem Unidade Construtiva";

export function ucLabel(name: string): string {
  return name.trim() || UC_SEM_LABEL;
}

/** Filtra linhas de orçamento/apropriação por unidade construtiva (nome exibido). */
export function filterByUnidadeConstrutiva<T extends { buildingUnitName?: string }>(
  rows: T[],
  selected: string
): T[] {
  if (!selected || selected === "all") return rows;
  return rows.filter((r) => ucLabel(r.buildingUnitName ?? "") === selected);
}

/** Lista ordenada de unidades construtivas presentes nos dados. */
export function listUnidadesConstrutivas(
  budgetResources: BudgetResource[],
  appropriated: AppropriatedResource[]
): string[] {
  const set = new Set<string>();
  for (const r of budgetResources) set.add(ucLabel(r.buildingUnitName));
  for (const r of appropriated) set.add(ucLabel(r.buildingUnitName));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

/** Para cada nome de UC (rótulo Sienge), conjunto de `buildingUnitId` vistos no orçado/apropriado. */
export function mapUnidadeConstrutivaNamesToBuildingUnitIds(
  budgetResources: BudgetResource[],
  appropriated: AppropriatedResource[],
  ucNames: string[]
): Map<string, Set<string>> {
  const nameToIds = new Map<string, Set<string>>();
  for (const n of ucNames) nameToIds.set(n, new Set());
  const add = (buildingUnitName: string | undefined, buildingUnitId: string | undefined) => {
    const lab = ucLabel(buildingUnitName ?? "");
    const id = String(buildingUnitId ?? "").trim();
    if (!id || !nameToIds.has(lab)) return;
    nameToIds.get(lab)!.add(id);
  };
  for (const r of budgetResources) add(r.buildingUnitName, r.buildingUnitId);
  for (const r of appropriated) add(r.buildingUnitName, r.buildingUnitId);
  return nameToIds;
}

function groupSum<T>(
  items: T[],
  key:   (item: T) => string,
  val:   (item: T) => number
): Map<string, number> {
  const m = new Map<string, number>();
  for (const item of items) {
    const k = key(item);
    m.set(k, (m.get(k) ?? 0) + val(item));
  }
  return m;
}

/* ─── KPIs ─────────────────────────────────────────────────────────────────── */

export function computeKpis(data: OrcamentoData) {
  const totalOrcado    = data.budgetResources.reduce((s, r) => s + r.totalOrcado, 0);
  const totalRealizado = data.appropriated.reduce((s, r) => s + r.value, 0);
  const pctRealizado   = totalOrcado > 0 ? (totalRealizado / totalOrcado) * 100 : 0;
  const saldo          = totalOrcado - totalRealizado;
  return { totalOrcado, totalRealizado, pctRealizado, saldo };
}

/* ─── Por Unidade Construtiva ──────────────────────────────────────────────── */

export function computeOrcadoRealizadoPorCentro(
  budgetResources: BudgetResource[],
  appropriated:    AppropriatedResource[]
): CCStat[] {
  const orcadoMap    = groupSum(budgetResources, (r) => ucLabel(r.buildingUnitName), (r) => r.totalOrcado);
  const realizadoMap = groupSum(appropriated,    (r) => ucLabel(r.buildingUnitName), (r) => r.value);

  const allCCs = new Set([...orcadoMap.keys(), ...realizadoMap.keys()]);

  return Array.from(allCCs)
    .map((cc) => {
      const orcado    = orcadoMap.get(cc)    ?? 0;
      const realizado = realizadoMap.get(cc) ?? 0;
      const saldo     = orcado - realizado;
      const pct       = orcado > 0 ? (realizado / orcado) * 100 : 0;
      return { ccName: cc, orcado, realizado, saldo, pct };
    })
    .filter((row) => row.orcado > 0 || row.realizado > 0)
    .sort((a, b) => b.orcado - a.orcado);
}

/* ─── Distribuição do orçado por CC (para pizza) ──────────────────────────── */

export function computeDistribuicaoOrcado(budgetResources: BudgetResource[]) {
  const m = groupSum(budgetResources, (r) => ucLabel(r.buildingUnitName), (r) => r.totalOrcado);
  return Array.from(m.entries())
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/** Chave insumo × UC — evita fundir o mesmo código em UCs diferentes quando o filtro é "Todas". */
function insumoUcKey(resourceId: string, buildingUnitName: string | undefined): string {
  const rid = (resourceId || "").trim();
  const cc = ucLabel(buildingUnitName ?? "");
  return `${rid}\x1f${cc}`;
}

/* ─── Top Insumos ──────────────────────────────────────────────────────────── */

export function computeTopInsumos(
  budgetResources: BudgetResource[],
  appropriated: AppropriatedResource[],
  n = 15
): InsumoStat[] {
  type Bucket = {
    resourceId: string;
    description: string;
    ccName: string;
    buildingUnitId: string;
    orcado: number;
    qtdOrcada: number;
    unit: string;
    imageUrl?: string;
  };
  const budgetMap = new Map<string, Bucket>();

  for (const r of budgetResources) {
    const rid = r.resourceId || r.description;
    if (!rid) continue;
    const cc = ucLabel(r.buildingUnitName);
    const key = insumoUcKey(rid, r.buildingUnitName);
    const buId = (r.buildingUnitId ?? "").trim();
    const existing = budgetMap.get(key);
    const u = (r.unit ?? "").trim();
    const img = r.imageUrl;
    if (existing) {
      existing.orcado += r.totalOrcado;
      existing.qtdOrcada += r.quantity;
      if (!existing.unit && u) existing.unit = u;
      if (!existing.imageUrl && img) existing.imageUrl = img;
      if (!existing.buildingUnitId && buId) existing.buildingUnitId = buId;
    } else {
      budgetMap.set(key, {
        resourceId: rid,
        description: r.description,
        ccName: cc,
        buildingUnitId: buId,
        orcado: r.totalOrcado,
        qtdOrcada: r.quantity,
        unit: u,
        ...(img ? { imageUrl: img } : {}),
      });
    }
  }

  const realizadoMap = new Map<string, number>();
  const qtdRealizadaMap = new Map<string, number>();
  for (const r of appropriated) {
    const rid = r.resourceId || r.description;
    if (!rid) continue;
    const cc = ucLabel(r.buildingUnitName);
    const key = insumoUcKey(rid, r.buildingUnitName);
    realizadoMap.set(key, (realizadoMap.get(key) ?? 0) + r.value);
    qtdRealizadaMap.set(key, (qtdRealizadaMap.get(key) ?? 0) + r.quantity);
    if (!budgetMap.has(key)) {
      const buIdA = (r.buildingUnitId ?? "").trim();
      budgetMap.set(key, {
        resourceId: rid,
        description: r.description,
        ccName: cc,
        buildingUnitId: buIdA,
        orcado: 0,
        qtdOrcada: 0,
        unit: (r.unit ?? "").trim(),
        ...(r.imageUrl ? { imageUrl: r.imageUrl } : {}),
      });
    } else {
      const b = budgetMap.get(key)!;
      const u = (r.unit ?? "").trim();
      if (!b.unit && u) b.unit = u;
      if (!b.imageUrl && r.imageUrl) b.imageUrl = r.imageUrl;
      const buIdA = (r.buildingUnitId ?? "").trim();
      if (!b.buildingUnitId && buIdA) b.buildingUnitId = buIdA;
    }
  }

  return Array.from(budgetMap.entries())
    .map(([key, { description, ccName, buildingUnitId, orcado, resourceId, qtdOrcada, unit, imageUrl }]) => {
      const realizado = realizadoMap.get(key) ?? 0;
      const qtdRealizada = qtdRealizadaMap.get(key) ?? 0;
      const saldo = orcado - realizado;
      const pct = orcado > 0 ? (realizado / orcado) * 100 : 0;
      const saldoQtd = qtdOrcada - qtdRealizada;
      const pctQtd = qtdOrcada > 0 ? (qtdRealizada / qtdOrcada) * 100 : 0;
      return {
        resourceId,
        description,
        ccName,
        ...(buildingUnitId ? { buildingUnitId } : {}),
        unit,
        qtdOrcada,
        qtdRealizada,
        saldoQtd,
        pctQtd,
        orcado,
        realizado,
        saldo,
        pct,
        ...(imageUrl ? { imageUrl } : {}),
      };
    })
    .filter((item) => item.orcado > 0 || item.realizado > 0)
    .sort((a, b) => b.orcado - a.orcado)
    .slice(0, n);
}

/** Retorna todos os insumos (sem limite). */
export function computeAllInsumos(
  budgetResources: BudgetResource[],
  appropriated:    AppropriatedResource[]
): InsumoStat[] {
  return computeTopInsumos(budgetResources, appropriated, 9999);
}

/** Cor do percentual de execução: verde < 80 %, amarelo 80-100 %, vermelho > 100 %. */
export function pctColor(pct: number): string {
  if (pct > 100) return "#ef4444";
  if (pct >= 80)  return "#f59e0b";
  return "#10b981";
}

/** Barra / legenda «Detalhamento por Insumo»: cinza ≤0%; verde <90%; âmbar 90–110%; vermelho >110%. */
export function pctDetalheInsumoBarColor(pct: number): string {
  if (!Number.isFinite(pct) || pct <= 0) return "#cbd5e1";
  if (pct < 90) return "#22c55e";
  if (pct <= 110) return "#eab308";
  return "#ef4444";
}

export function pctDetalheInsumoTextClass(pct: number): string {
  if (!Number.isFinite(pct) || pct <= 0) return "text-slate-400";
  if (pct < 90) return "text-emerald-700";
  if (pct <= 110) return "text-amber-700";
  return "text-red-600";
}

export function pctDetalheInsumoPillClass(pct: number): string {
  if (!Number.isFinite(pct) || pct <= 0) return "bg-slate-100 text-slate-600";
  if (pct < 90) return "bg-emerald-100 text-emerald-800";
  if (pct <= 110) return "bg-amber-100 text-amber-900";
  return "bg-red-100 text-red-800";
}
